import { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableColumn, TableRowData } from "@/components/ui/table";
import { cn } from "@/lib/class-control";
import { BankStatementTransaction } from "@/types/bank-statement";
import InputSearch from "@/components/ui/input-search";
import ButtonFilter from "@/components/ui/button-filter";
import { AcoesLinha } from "@/pages/relacao-pagamentos/components/acoes-linha";

type TableVariant = "default" | "demonstrativo";

interface BankStatementTableExtratoProps {
  data: BankStatementTransaction[];
  activeTab?: "Todos" | "Entradas" | "Saídas" | "Incentivos";
  variant?: TableVariant;
  onView: (transaction: BankStatementTransaction) => void;
  onEdit: (transaction: BankStatementTransaction) => void;
  onDelete: (transaction: BankStatementTransaction) => void;
  onFilterClick: () => void;
  onLaunchOperationClick?: () => void;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

const formatBRL = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const matchesCategory = (transaction: BankStatementTransaction, token: string) =>
  (transaction.category || "").toUpperCase().includes(token);

const getCreditorLabel = (transaction: BankStatementTransaction) =>
  transaction.creditorName || transaction.description || "-";

const getCreditorInitial = (transaction: BankStatementTransaction) =>
  (
    transaction.creditorName ||
    transaction.description ||
    "?"
  )
    .charAt(0)
    .toUpperCase();

const getActionLabel = (transaction: BankStatementTransaction, base: string) => {
  const id = transaction.number?.trim() || String(transaction.id);
  return `${base} transação ${id}`;
};

const DEFAULT_COLUMNS: TableColumn[] = [
  { key: "paymentDate", label: "Data do Pagamento", width: "min-w-[136px]" },
  { key: "description", label: "II.b. HISTÓRICO", width: "min-w-[127px]" },
  { key: "incentive", label: "Fomento Depósitos", width: "min-w-[144px]" },
  { key: "otherDeposits", label: "Outros Depósitos", width: "min-w-[131px]" },
  { key: "transfers", label: "Transferências Rec/Conc", width: "min-w-[187px]" },
  { key: "application", label: "Aplic/Resg", width: "min-w-[100px]" },
  { key: "payments", label: "Pagamentos", width: "min-w-[100px]" },
  { key: "expenses", label: "Despesas Bancárias", width: "min-w-[150px]" },
  { key: "actions", label: "Ações", width: "min-w-[100px]" },
];

const DEMONSTRATIVO_COLUMNS: TableColumn[] = [
  { key: "paymentDate", label: "Data do Pagamento", width: "min-w-[136px]" },
  { key: "incentive", label: "Fomento Depósitos", width: "min-w-[144px]" },
  { key: "otherDeposits", label: "Outros Depósitos", width: "min-w-[131px]" },
  { key: "transfers", label: "Transferências Rec/Conc", width: "max-w-[187px]" },
  { key: "bank", label: "Banco", width: "min-w-[100px]" },
  { key: "creditor", label: "Credor", width: "max-w-[160px]" },
  { key: "amount", label: "Valor", width: "max-w-[90px]" },
  { key: "actions", label: "Ações", width: "min-w-[120px]" },
];

function DesktopActions({
  transaction,
  onView,
  onEdit,
  onDelete,
}: {
  transaction: BankStatementTransaction;
  onView: (t: BankStatementTransaction) => void;
  onEdit: (t: BankStatementTransaction) => void;
  onDelete: (t: BankStatementTransaction) => void;
}) {
  return (
    <div className="flex items-center gap-[10px]">
      <AcoesLinha 
        onView={() => onView(transaction)} 
        onEdit={() => onEdit(transaction)} 
        onDelete={() => onDelete(transaction)} 
      />
    </div>
  );
}

function MobileActions({
  transaction,
  onView,
  onEdit,
  onDelete,
}: {
  transaction: BankStatementTransaction;
  onView: (t: BankStatementTransaction) => void;
  onEdit: (t: BankStatementTransaction) => void;
  onDelete: (t: BankStatementTransaction) => void;
}) {
  return (
    <div className="flex items-center gap-[10px]">
      <AcoesLinha 
        onView={() => onView(transaction)} 
        onEdit={() => onEdit(transaction)} 
        onDelete={() => onDelete(transaction)} 
      />
    </div>
  );
}

function CreditorCell({
  transaction,
}: {
  transaction: BankStatementTransaction;
}) {
  const label = getCreditorLabel(transaction);
  const fullLabel = transaction.creditorName || "-";
  return (
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 flex items-center justify-center rounded-full bg-[#7846ff] text-xs font-bold text-white">
        {getCreditorInitial(transaction)}
      </div>
      <span aria-hidden="true" className="truncate text-white" title={fullLabel}>
        {label}
      </span>
      <span className="sr-only">{fullLabel}</span>
    </div>
  );
}

function DescriptionCell({ value }: { value: string }) {
  const text = value || "-";
  return (
    <>
      <span aria-hidden="true" className="line-clamp-2 break-words" title={text}>
        {text}
      </span>
      <span className="sr-only">{text}</span>
    </>
  );
}

function buildDefaultRow(
  transaction: BankStatementTransaction,
  onView: (t: BankStatementTransaction) => void,
  onEdit: (t: BankStatementTransaction) => void,
  onDelete: (t: BankStatementTransaction) => void,
): TableRowData {
  const dash = "-";
  const formattedAmount = formatBRL(transaction.amount);
  return {
    paymentDate: transaction.paymentDate,
    description: <DescriptionCell value={transaction.description} />,
    incentive: matchesCategory(transaction, "FOMENTO DEP") ? formattedAmount : dash,
    otherDeposits: matchesCategory(transaction, "OUTROS DEP") ? formattedAmount : dash,
    transfers: matchesCategory(transaction, "TRANSFER") ? formattedAmount : dash,
    application: matchesCategory(transaction, "APLICA") ? formattedAmount : dash,
    payments: matchesCategory(transaction, "PAGAMENTOS") ? formattedAmount : dash,
    expenses: matchesCategory(transaction, "DESPESAS") ? formattedAmount : dash,
    actions: (
      <DesktopActions
        transaction={transaction}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ),
  };
}

function buildDemonstrativoRow(
  transaction: BankStatementTransaction,
  onView: (t: BankStatementTransaction) => void,
  onEdit: (t: BankStatementTransaction) => void,
  onDelete: (t: BankStatementTransaction) => void,
): TableRowData {
  const dash = "--";
  const formattedAmount = formatBRL(transaction.amount);
  return {
    paymentDate: transaction.paymentDate,
    incentive: matchesCategory(transaction, "FOMENTO DEP") ? formattedAmount : dash,
    otherDeposits: matchesCategory(transaction, "OUTROS DEP") ? formattedAmount : dash,
    transfers: matchesCategory(transaction, "TRANSFER") ? formattedAmount : dash,
    bank: "-",
    creditor: <CreditorCell transaction={transaction} />,
    amount: formattedAmount,
    actions: (
      <DesktopActions
        transaction={transaction}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ),
  };
}

function DefaultMobileCard({
  transaction,
  onView,
  onEdit,
  onDelete,
}: {
  transaction: BankStatementTransaction;
  onView: (t: BankStatementTransaction) => void;
  onEdit: (t: BankStatementTransaction) => void;
  onDelete: (t: BankStatementTransaction) => void;
}) {
  const dash = "-";
  const formattedAmount = formatBRL(transaction.amount);
  const description = transaction.description || "-";
  return (
    <article
      className="flex flex-col gap-3 rounded-2xl border border-[#0c1427]/60 bg-[#0c1427]/40 p-4 text-white"
      data-testid="extrato-card-default"
    >
      <header className="flex items-center justify-between text-sm text-gray-300">
        <span>{transaction.paymentDate}</span>
      </header>
      <DescriptionCell value={description} />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <dt className="text-gray-400">Fomento Depósitos</dt>
          <dd>{matchesCategory(transaction, "FOMENTO DEP") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Outros Depósitos</dt>
          <dd>{matchesCategory(transaction, "OUTROS DEP") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Transferências</dt>
          <dd>{matchesCategory(transaction, "TRANSFER") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Aplic/Resg</dt>
          <dd>{matchesCategory(transaction, "APLICA") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Pagamentos</dt>
          <dd>{matchesCategory(transaction, "PAGAMENTOS") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Despesas Bancárias</dt>
          <dd>{matchesCategory(transaction, "DESPESAS") ? formattedAmount : dash}</dd>
        </div>
      </dl>
      <footer className="flex justify-end">
        <MobileActions
          transaction={transaction}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </footer>
    </article>
  );
}

function DemonstrativoMobileCard({
  transaction,
  onView,
  onEdit,
  onDelete,
}: {
  transaction: BankStatementTransaction;
  onView: (t: BankStatementTransaction) => void;
  onEdit: (t: BankStatementTransaction) => void;
  onDelete: (t: BankStatementTransaction) => void;
}) {
  const dash = "--";
  const formattedAmount = formatBRL(transaction.amount);
  return (
    <article
      className="flex flex-col gap-3 rounded-2xl border border-[#0c1427]/60 bg-[#0c1427]/40 p-4 text-white"
      data-testid="extrato-card-demonstrativo"
    >
      <header className="flex items-center justify-between text-sm text-gray-300">
        <span>{transaction.paymentDate}</span>
        <span className="text-[#13d480] font-semibold">{formattedAmount}</span>
      </header>
      <CreditorCell transaction={transaction} />
      <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-xs">
        <div>
          <dt className="text-gray-400">Fomento</dt>
          <dd>{matchesCategory(transaction, "FOMENTO DEP") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Outros</dt>
          <dd>{matchesCategory(transaction, "OUTROS DEP") ? formattedAmount : dash}</dd>
        </div>
        <div>
          <dt className="text-gray-400">Transf.</dt>
          <dd>{matchesCategory(transaction, "TRANSFER") ? formattedAmount : dash}</dd>
        </div>
      </dl>
      <footer className="flex justify-end">
        <MobileActions
          transaction={transaction}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </footer>
    </article>
  );
}

export function BankStatementTableExtrato({
  data,
  variant = "default",
  onView,
  onEdit,
  onDelete,
  onFilterClick,
  onLaunchOperationClick,
  currentPage = 1,
  totalPages = 1,
  totalItems,
  onPageChange,
}: BankStatementTableExtratoProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(
      (transaction) =>
        (transaction.creditorName?.toLowerCase() || "").includes(term) ||
        (transaction.description?.toLowerCase() || "").includes(term) ||
        transaction.paymentDate.includes(term),
    );
  }, [data, searchTerm]);

  const visibleData = searchTerm ? filteredData : data;
  const isEmpty = visibleData.length === 0;

  const columns = variant === "demonstrativo" ? DEMONSTRATIVO_COLUMNS : DEFAULT_COLUMNS;
  const tableRows: TableRowData[] = visibleData.map((transaction) =>
    variant === "demonstrativo"
      ? buildDemonstrativoRow(transaction, onView, onEdit, onDelete)
      : buildDefaultRow(transaction, onView, onEdit, onDelete),
  );

  const goPrev = () => onPageChange?.(Math.max(1, currentPage - 1));
  const goNext = () => onPageChange?.(Math.min(totalPages, currentPage + 1));
  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= totalPages || totalPages === 0;

  return (
    <section className="rounded-[20px] bg-darkblue p-2" aria-label="Extrato do período">
      <header className="mb-[40px] flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h3 className="text-lg font-bold text-white">Extrato do Período</h3>

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-[10px]">
          <InputSearch
            focus="primary"
            positionIcon="end"
            ariaLabel="Buscar transações"
            value={searchTerm}
            onChange={setSearchTerm}
            classNameContainer="md:w-52 w-full"
            classNameIcon="text-primary"
          />
          <ButtonFilter
            positionIcon="end"
            onClick={onFilterClick}
            className="w-full justify-around text-xs text-white md:w-24"
            classNameIcon="text-primary"
          />
          {variant === "default" && onLaunchOperationClick && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onLaunchOperationClick}
              className="w-full md:w-auto"
            >
              Lançar Operação
            </Button>
          )}
        </div>
      </header>

      <p className="sr-only" aria-live="polite">
        {`Exibindo ${visibleData.length} de ${totalItems} transações, página ${currentPage} de ${totalPages}.`}
      </p>

      {isEmpty ? (
        <div data-testid="extrato-empty-state" className="py-8 text-md text-center text-gray-400">
          Nenhuma transação encontrada.
        </div>
      ) : (
        <>
          <div className="hidden md:block" data-testid="extrato-table-desktop">
            <Table
              columns={columns}
              data={tableRows}
              rowClassName="h-[79px]"
            />
          </div>
          <div
            className="flex flex-col gap-3 md:hidden"
            data-testid="extrato-cards-mobile"
          >
            {visibleData.map((transaction) =>
              variant === "demonstrativo" ? (
                <DemonstrativoMobileCard
                  key={transaction.id}
                  transaction={transaction}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ) : (
                <DefaultMobileCard
                  key={transaction.id}
                  transaction={transaction}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ),
            )}
          </div>
        </>
      )}

      <nav
        aria-label="Paginação do extrato"
        className="mt-6 flex w-full min-w-0 flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between"
      >
        <p className="text-xs font-normal text-[#888888]">
          Exibindo {visibleData.length} de {totalItems} transações
        </p>

        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto md:flex-nowrap">
          <button
            type="button"
            onClick={goPrev}
            disabled={prevDisabled}
            className={cn(
              "min-h-10 min-w-10 flex items-center justify-center rounded-[20px] rotate-180 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              prevDisabled ? "cursor-not-allowed opacity-50" : "hover:bg-white/10",
            )}
            aria-label="Página anterior"
          >
            <ChevronRight className="h-2 w-2 text-white" />
          </button>

          <span
            aria-hidden="true"
            className="px-2 text-xs text-white md:hidden"
          >
            {`Página ${currentPage} de ${totalPages}`}
          </span>
          <span className="sr-only">
            {`Página ${currentPage} de ${totalPages}`}
          </span>

          <span className="hidden items-center gap-2 md:flex" aria-hidden="true">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 5) return true;
                if (page === 1 || page === totalPages) return true;
                return Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => {
                if (index > 0 && page - array[index - 1] > 1) {
                  return (
                    <span key={`ellipsis-${page}`} className="mx-1 text-white">
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => onPageChange?.(page)}
                    aria-label={`Página ${page}`}
                    aria-current={currentPage === page ? "page" : undefined}
                    className={cn(
                      "min-h-8 min-w-8 rounded-[20px] text-xs font-normal flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      currentPage === page
                        ? "bg-purple-500 font-bold text-white"
                        : "text-white hover:bg-white/10",
                    )}
                  >
                    {page}
                  </button>
                );
              })}
          </span>

          <button
            type="button"
            onClick={goNext}
            disabled={nextDisabled}
            className={cn(
              "min-h-10 min-w-10 flex items-center justify-center rounded-[20px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              nextDisabled ? "cursor-not-allowed opacity-50" : "hover:bg-white/10",
            )}
            aria-label="Próxima página"
          >
            <ChevronRight className="h-2 w-2 text-white" />
          </button>
        </div>
      </nav>
    </section>
  );
}

BankStatementTableExtrato.displayName = "BankStatementTableExtrato";
